import React, { FC, memo, useMemo, ReactNode } from 'react';
import {
  OwcEditableDropdown,
  OwcListItem,
  OwcTypography,
  OwcProgressSpinner
} from '@one/react';
import {
  UserOption,
  buildUserOptions,
  UserOptionsConfig
} from '../../../utils/helpers/user/userOptionsBuilder';
import { UserData } from '../../../utils/helpers/text/userFormatting';
import { react18WorkaroundProps } from '../../../utils/constants/constants';

interface UserSelectDropdownProps {
  value: string | UserData | UserOption | null | '';
  users: UserData[];
  onChange: (selected: UserOption | null, user?: UserData) => void;
  optionsConfig?: UserOptionsConfig;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  loadingComponent?: ReactNode;
  clearable?: boolean;
  width?: number | string;
  style?: React.CSSProperties;
  dataTestid?: string;
  name?: string;
  dropdownProps?: Record<string, any>;
  emptyComponent?: ReactNode;
  emptyText?: string;
  returnUserData?: boolean;
  keyExtractor?: (option: UserOption, index: number) => string;
}

/**
 * Generic User Selection Dropdown
*/
const UserSelectDropdownImpl: FC<UserSelectDropdownProps> = ({
  value,
  users,
  onChange,
  optionsConfig,
  label = "Select user",
  placeholder = "Type to search users",
  required = false,
  disabled = false,
  loading = false,
  loadingText = "Loading...",
  loadingComponent,
  clearable = true,
  width = 200,
  style = {},
  dataTestid = "user-select-dropdown",
  name = "userSelect",
  dropdownProps = {},
  emptyComponent,
  emptyText = "No users available",
  returnUserData = true,
  keyExtractor
}) => {
  const options = useMemo(() => {
    if (!users || users.length === 0) return [];
    return buildUserOptions(users, optionsConfig);
  }, [users, optionsConfig]);

  const getDisplayValue = (): string | null => {
    if (!value) return null;

    if (typeof value === 'string') {
      if (value === '') {
        const allUsersOption = options.find(opt => opt.name === '');
        return allUsersOption?.label || null;
      }
      const option = options.find(opt => opt.name === value);
      return option?.label || value;
    }

    if (typeof value === 'object') {
      if ('label' in value && value.label) {
        return value.label;
      }
      if ('userId' in value && value.userId) {
        const option = options.find(opt => opt.name === value.userId);
        return option?.label || null;
      }
    }

    return null;
  };

  const isOptionSelected = (option: UserOption): boolean => {
    if (!value) return false;

    if (typeof value === 'string') {
      return option.name === value;
    }

    if (typeof value === 'object' && 'userId' in value) {
      return option.name === (value as UserData).userId;
    }

    if (typeof value === 'object' && 'name' in value) {
      return option.name === (value as UserOption).name;
    }

    return false;
  };

  const handleSelectChange = (ev: any) => {
    if (ev.detail !== null && ev.detail !== undefined) {
      // Prefer resolving by the dropdown's own resolved display value (mirrors what
      // the vendor component itself uses internally) over indexing `options` directly,
      // since `options` can be recomputed with a different order/length than the
      // already-open menu's DOM if `users` changes mid-interaction.
      const resolvedLabel = ev.target?.value;
      const selectedOption =
        (resolvedLabel && options.find((opt) => opt.label === resolvedLabel)) ||
        options[ev.detail];
      if (selectedOption) {
        const userData = returnUserData && selectedOption.name
          ? users.find(u => u.userId === selectedOption.name)
          : undefined;

        onChange(selectedOption, userData);
      }
    } else if (clearable) {
      onChange(null);
    }
  };

  const handleInputKeyDown = (ev: any) => {
    const inputValue = ev.target?.value || "";
    if (clearable && inputValue.trim() === "" && (ev.detail === "Enter" || ev.detail === "Tab")) {
      onChange(null);
    }
  };

  const getItemKey = (option: UserOption, index: number): string => {
    if (keyExtractor) {
      return keyExtractor(option, index);
    }
    return option.name || `option-${index}`;
  };

  // Memoize the rendered list items. Without this, parent re-renders (and
  // there are many: Mobiscroll Eventcalendar v5.35 internally bumps its
  // own "update" counter ~16x/s, propagating through context consumers)
  // would re-render 1000+ <OwcListItem> rows every time. This single
  // useMemo is the cheapest big win in the whole app while the parent
  // tree is being shaken: as long as `options`, `value` and the key
  // extractor are stable, React reuses the same element references and
  // skips reconciliation entirely.
  const renderedItems = useMemo(() => {
    return options.map((option, index) => (
      <OwcListItem
        key={getItemKey(option, index)}
        value={option.label}
        selected={isOptionSelected(option)}
        data-testid={`${dataTestid}-item-${option.name || 'empty'}`}
        style={{ whiteSpace: "normal", lineHeight: "1.35", height: "auto" }}
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        {option.label}
      </OwcListItem>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value, dataTestid]);

  const resolvedDisplayValue = getDisplayValue();
  const controlledDropdownValue = resolvedDisplayValue === null ? "" : resolvedDisplayValue;

  if (loading) {
    return (
      <OwcEditableDropdown
        {...react18WorkaroundProps}
        placeholder={placeholder}
        style={{ width, ...style }}
        label={label}
        data-testid={dataTestid}
        name={name}
        value={loadingText}
        disabled={true}
        {...dropdownProps}
      >
        {loadingComponent || (
          <OwcListItem value={loadingText} style={{ marginLeft: "auto", marginRight: "auto" }} {...react18WorkaroundProps}>
            <OwcTypography variant="caption" {...react18WorkaroundProps}>
              <OwcProgressSpinner diameter={24} {...react18WorkaroundProps} />
            </OwcTypography>
          </OwcListItem>
        )}
      </OwcEditableDropdown>
    );
  }

  if ((!options || options.length === 0) && emptyComponent) {
    return (
      <OwcEditableDropdown
        {...react18WorkaroundProps} style={{ width, ...style }}
        label={label}
        data-testid={dataTestid}
        name={name}
        value=""
        disabled={true}
        {...dropdownProps}>
        {emptyComponent}
      </OwcEditableDropdown>
    );
  }

  if (!options || options.length === 0) {
    return (
      <OwcEditableDropdown
        {...react18WorkaroundProps}
        placeholder={placeholder} style={{ width, ...style }}
        label={label}
        data-testid={dataTestid}
        name={name}
        value=""
        disabled={true}
        {...dropdownProps}>
        <OwcListItem value={emptyText} disabled {...react18WorkaroundProps}>
          <OwcTypography variant="caption" {...react18WorkaroundProps}>{emptyText}</OwcTypography>
        </OwcListItem>
      </OwcEditableDropdown>
    );
  }

  return (
    <OwcEditableDropdown
      key={`${name}-${controlledDropdownValue}`}
      {...react18WorkaroundProps} style={{ width, ...style }}
      label={label}
      data-testid={dataTestid}
      name={name}
      value={controlledDropdownValue}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      onSelectChange={handleSelectChange}
      onInputKeyDown={handleInputKeyDown}
      {...dropdownProps}    >
      {renderedItems}
    </OwcEditableDropdown>
  );
};
UserSelectDropdownImpl.displayName = 'UserSelectDropdown';

// Wrap the named component with memo PLUS an explicit shallow compare.
// CRA's react-refresh-babel transform strips memo() in dev mode when no
// second argument is provided (it treats single-arg memo as a Fast
// Refresh boundary and unwraps it), which would defeat memoization for
// the long list of OwcListItem children below.
const shallowEqualProps = (prev: UserSelectDropdownProps, next: UserSelectDropdownProps) => {
  const keys: (keyof UserSelectDropdownProps)[] = [
    'value', 'users', 'onChange', 'optionsConfig', 'label', 'placeholder',
    'required', 'disabled', 'loading', 'loadingText', 'loadingComponent',
    'clearable', 'width', 'style', 'dataTestid', 'name', 'dropdownProps',
    'emptyComponent', 'emptyText', 'returnUserData', 'keyExtractor',
  ];
  for (const k of keys) {
    if (prev[k] !== next[k]) return false;
  }
  return true;
};

const UserSelectDropdown = memo(UserSelectDropdownImpl, shallowEqualProps);

export default UserSelectDropdown;
